import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { AccountsProvider } from "./lib/accounts";
import { TourProvider } from "./lib/tour";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import RegisterPage from "./pages/RegisterPage";
import TransactionForm from "./pages/TransactionForm";
import SnapReceipt from "./pages/SnapReceipt";
import Reconcile from "./pages/Reconcile";
import CsvDiff from "./pages/CsvDiff";
import Forecast from "./pages/Forecast";
import Recurring from "./pages/Recurring";
import Import from "./pages/Import";
import ImportHistory from "./pages/ImportHistory";
import BulkAdd from "./pages/BulkAdd";
import Budgets from "./pages/Budgets";
import Goals from "./pages/Goals";
import Duplicates from "./pages/Duplicates";
import Accounts from "./pages/Accounts";
import Admin from "./pages/Admin";
import Upgrade from "./pages/Upgrade";
import UpgradeSuccess from "./pages/UpgradeSuccess";
import UpgradeCancel from "./pages/UpgradeCancel";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Profile from "./pages/Profile";
import MerchantRules from "./pages/MerchantRules";
import TourVideos from "./pages/TourVideos";
import More from "./pages/More";
import BankSync from "./pages/BankSync";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import SmsSignup from "./pages/SmsSignup";
import SmsReview from "./pages/SmsReview";
import SmsTerms from "./pages/SmsTerms";
import Help from "./pages/Help";
import PwaInstallPrompt from "./components/PwaInstallPrompt";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="nb-tag">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <AccountsProvider>
      <TourProvider>
        <Layout>{children}</Layout>
        <PwaInstallPrompt />
      </TourProvider>
    </AccountsProvider>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                border: "2px solid #1a1a1a",
                borderRadius: 12,
                background: "#fff",
                fontWeight: 700,
                boxShadow: "4px 4px 0 0 #1a1a1a",
              },
            }}
          />
          <Routes>
            <Route
              path="/login"
              element={
                <PublicOnly>
                  <Login />
                </PublicOnly>
              }
            />
            <Route
              path="/register-account"
              element={
                <PublicOnly>
                  <Register />
                </PublicOnly>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicOnly>
                  <ForgotPassword />
                </PublicOnly>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicOnly>
                  <ResetPassword />
                </PublicOnly>
              }
            />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-signup" element={<SmsSignup />} />
            <Route path="/sms-review" element={<SmsReview />} />
            <Route path="/sms-terms" element={<SmsTerms />} />
            <Route
              path="/"
              element={
                <Protected>
                  <Dashboard />
                </Protected>
              }
            />
            <Route
              path="/register"
              element={
                <Protected>
                  <RegisterPage />
                </Protected>
              }
            />
            <Route
              path="/add"
              element={
                <Protected>
                  <TransactionForm />
                </Protected>
              }
            />
            <Route
              path="/transactions/bulk-add"
              element={
                <Protected>
                  <BulkAdd />
                </Protected>
              }
            />
            <Route
              path="/help"
              element={
                <Protected>
                  <Help />
                </Protected>
              }
            />
            <Route
              path="/txn/:id"
              element={
                <Protected>
                  <TransactionForm />
                </Protected>
              }
            />
            <Route
              path="/snap"
              element={
                <Protected>
                  <SnapReceipt />
                </Protected>
              }
            />
            <Route
              path="/reconcile"
              element={
                <Protected>
                  <Reconcile />
                </Protected>
              }
            />
            <Route
              path="/reconcile/csv-diff"
              element={
                <Protected>
                  <CsvDiff />
                </Protected>
              }
            />
            <Route
              path="/forecast"
              element={
                <Protected>
                  <Forecast />
                </Protected>
              }
            />
            <Route
              path="/recurring"
              element={
                <Protected>
                  <Recurring />
                </Protected>
              }
            />
            <Route
              path="/import"
              element={
                <Protected>
                  <Import />
                </Protected>
              }
            />
            <Route
              path="/import/history"
              element={
                <Protected>
                  <ImportHistory />
                </Protected>
              }
            />
            <Route
              path="/budgets"
              element={
                <Protected>
                  <Budgets />
                </Protected>
              }
            />
            <Route
              path="/goals"
              element={
                <Protected>
                  <Goals />
                </Protected>
              }
            />
            <Route
              path="/duplicates"
              element={
                <Protected>
                  <Duplicates />
                </Protected>
              }
            />
            <Route
              path="/accounts"
              element={
                <Protected>
                  <Accounts />
                </Protected>
              }
            />
            <Route
              path="/admin"
              element={
                <Protected>
                  <Admin />
                </Protected>
              }
            />
            <Route
              path="/upgrade"
              element={
                <Protected>
                  <Upgrade />
                </Protected>
              }
            />
            <Route
              path="/upgrade/success"
              element={
                <Protected>
                  <UpgradeSuccess />
                </Protected>
              }
            />
            <Route
              path="/upgrade/cancel"
              element={
                <Protected>
                  <UpgradeCancel />
                </Protected>
              }
            />
            <Route
              path="/profile"
              element={
                <Protected>
                  <Profile />
                </Protected>
              }
            />
            <Route
              path="/merchant-rules"
              element={
                <Protected>
                  <MerchantRules />
                </Protected>
              }
            />
            <Route
              path="/tour-videos"
              element={
                <Protected>
                  <TourVideos />
                </Protected>
              }
            />
            <Route
              path="/more"
              element={
                <Protected>
                  <More />
                </Protected>
              }
            />
            <Route
              path="/bank-sync"
              element={
                <Protected>
                  <BankSync />
                </Protected>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
